/**
 * ProgressTracker — Sticky plan visualization for multi-step agent tasks.
 *
 * Renders in the ViewportFooter, replacing StreamingStatus when a plan exists.
 * Shows plan title, progress bar, step list with status icons, and elapsed time.
 *
 * States:
 * - Live: steps executing (spinner + shimmer on active step)
 * - Receipt: all steps completed (muted, expandable)
 * - Interrupted: waiting for HITL approval (amber paused state)
 */

import { useEffect, useRef, useState } from 'react';
import {
  CheckIcon,
  ChevronDownIcon,
  CircleAlertIcon,
  ClockIcon,
  LoaderIcon,
  TimerIcon,
} from 'lucide-react';
import { cn } from '@/client/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlanStep {
  title: string;
  status: 'pending' | 'in_progress' | 'completed';
  description: string;
}

export interface ProgressTrackerProps {
  plan: {
    title: string;
    steps: PlanStep[];
    startedAt?: string;
    completedAt?: string | null;
  };
  /** Custom streaming status text from get_stream_writer — shown as subheader. */
  streamingStatus?: string | null;
  /** Whether the agent is currently running (show stop button + streaming even on completed plans). */
  isRunning?: boolean;
  /** Whether we're following another user's active run (no stop button). */
  isJoined?: boolean;
  /** Whether the thread is interrupted (HITL approval pending). */
  isInterrupted?: boolean;
  onStop?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatElapsedTime(ms: number): string {
  const seconds = Math.round(Math.max(0, ms) / 100) / 10;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds) % 60;
  return `${m}m ${s}s`;
}

/** Max visible steps before collapsing behind "N more steps" */
const COLLAPSE_THRESHOLD = 5;

/** Rotating placeholder labels when no custom streaming status is available. */
const DEFAULT_LABELS = ['Thinking', 'Reflecting', 'Reasoning', 'Synthesizing', 'Resolving'];

function useRotatingLabel(active: boolean, intervalMs = 3000): string {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * DEFAULT_LABELS.length));
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!active) return;
    setIndex(Math.floor(Math.random() * DEFAULT_LABELS.length));
    timerRef.current = setInterval(() => {
      setIndex((prev) => {
        let next: number;
        do { next = Math.floor(Math.random() * DEFAULT_LABELS.length); } while (next === prev);
        return next;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [active, intervalMs]);

  return DEFAULT_LABELS[index];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ status, isRunning, isInterrupted }: { status: PlanStep['status']; isRunning?: boolean; isInterrupted?: boolean }) {
  // In-progress step on an interrupted thread → awaiting approval
  if (status === 'in_progress' && isInterrupted) {
    return (
      <span
        className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-warning bg-background motion-safe:transition-all motion-safe:duration-200"
        aria-hidden
      >
        <ClockIcon className="size-2.5 text-warning" />
      </span>
    );
  }

  if (status === 'pending' || (status === 'in_progress' && !isRunning)) {
    // Pending, or in_progress but agent stopped — show as idle circle
    return (
      <span
        className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-border bg-background motion-safe:transition-all motion-safe:duration-200"
        aria-hidden
      />
    );
  }

  if (status === 'in_progress') {
    return (
      <span
        className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-primary bg-background shadow-[0_0_0_2px_hsl(262_60%_55%/0.1)] motion-safe:transition-all motion-safe:duration-300"
        aria-hidden
      >
        <LoaderIcon className="size-2.5 text-primary motion-safe:animate-spin" />
      </span>
    );
  }

  // completed
  return (
    <span
      className="flex size-4 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-75 motion-safe:duration-300"
      aria-hidden
    >
      <CheckIcon className="size-2.5" strokeWidth={3} />
    </span>
  );
}

function ElapsedTimeBadge({ startedAt, completedAt, isRunning }: { startedAt?: string; completedAt?: string | null; isRunning?: boolean }) {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    if (completedAt || !isRunning) return; // static — no ticking
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [startedAt, completedAt, isRunning]);

  if (!startedAt) return null;

  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : now;
  const elapsed = Math.max(0, end - start);

  return (
    <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
      <TimerIcon className="size-3" />
      {formatElapsedTime(elapsed)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Step Row
// ---------------------------------------------------------------------------

function StepRow({
  step,
  isActive,
  isLast,
  displayStatus,
  isRunning,
  isInterrupted,
}: {
  step: PlanStep;
  isActive: boolean;
  isLast: boolean;
  displayStatus?: string | null;
  isRunning?: boolean;
  isInterrupted?: boolean;
}) {
  const [descOpen, setDescOpen] = useState(false);
  const hasDesc = step.description.length > 0;
  const showDescByDefault = step.status === 'in_progress';
  const descVisible = showDescByDefault || descOpen;

  return (
    <li
      className={cn(
        'relative flex items-start gap-2.5 py-1.5',
        isActive && !isInterrupted && 'mx-[-8px] rounded-md bg-primary/[0.04] px-2',
        isActive && isInterrupted && 'mx-[-8px] rounded-md bg-warning/[0.06] px-2',
      )}
      aria-current={isActive ? 'step' : undefined}
    >
      {/* Connector line — stretches from below icon through to next step's icon */}
      {!isLast && (
        <div
          className={cn(
            'absolute -bottom-[8px] top-5 w-px bg-border',
            isActive ? 'left-[15.5px]' : 'left-[7.5px]',
          )}
          aria-hidden
        />
      )}

      <div className="relative z-10 mt-px">
        <StepIndicator status={step.status} isRunning={isRunning} isInterrupted={isActive && isInterrupted} />
      </div>

      <div className="min-w-0 flex-1">
        {step.status === 'completed' && hasDesc ? (
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-1 text-left"
            onClick={() => setDescOpen((v) => !v)}
          >
            <span className="flex-1 text-[13px] font-medium leading-snug text-foreground">
              {step.title}
            </span>
            <ChevronDownIcon
              className={cn(
                'size-3 shrink-0 text-muted-foreground opacity-40 transition-transform duration-150',
                descOpen && 'rotate-180',
              )}
            />
          </button>
        ) : (
          <span
            className={cn(
              'block text-[13px] font-medium leading-snug',
              step.status === 'in_progress' && isRunning && !isInterrupted && 'shimmer shimmer-speed-80 text-foreground',
              step.status === 'in_progress' && isInterrupted && 'text-warning',
              step.status === 'pending' && 'text-foreground',
              step.status === 'completed' && 'text-foreground',
            )}
          >
            {step.title}
          </span>
        )}

        {/* Description — interrupted, streaming, or step description */}
        {step.status === 'in_progress' && isInterrupted ? (
          <p className="mt-0.5 text-[11px] leading-snug font-medium text-warning">
            Awaiting approval
          </p>
        ) : step.status === 'in_progress' && displayStatus ? (
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground shimmer shimmer-speed-80">
            {displayStatus}
          </p>
        ) : hasDesc && descVisible ? (
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            {step.description}
          </p>
        ) : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// ProgressTracker
// ---------------------------------------------------------------------------

export function ProgressTracker({ plan, streamingStatus, isRunning, isJoined, isInterrupted, onStop, className }: ProgressTrackerProps) {
  const { steps, title, startedAt, completedAt } = plan;

  const totalSteps = steps.length;
  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const allDone = totalSteps > 0 && completedCount === totalSteps;
  const progressPct = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  // Rotating placeholder when agent is running but no custom streaming text
  const placeholderLabel = useRotatingLabel(!!isRunning && !streamingStatus);
  const displayStatus = streamingStatus || (isRunning ? placeholderLabel : null);

  // Find active step (first in_progress, else first pending)
  const activeIndex = steps.findIndex((s) => s.status === 'in_progress');

  // Collapse logic for long plans — only when not running
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = !isRunning && totalSteps > COLLAPSE_THRESHOLD;
  const visibleSteps = needsCollapse && !expanded ? steps.slice(0, COLLAPSE_THRESHOLD) : steps;
  const hiddenCount = needsCollapse && !expanded ? totalSteps - COLLAPSE_THRESHOLD : 0;

  // Collapsible body — user can expand/collapse at any time
  const [stepsOpen, setStepsOpen] = useState(true);

  // Show stop button when agent is running
  const showStop = isRunning && onStop;

  // Status badge text
  const statusBadge = allDone
    ? `All ${totalSteps} steps completed`
    : `${completedCount} of ${totalSteps} complete`;

  return (
    <div className={cn('rounded-lg border bg-background', className)} role="status" aria-live="polite" aria-busy={!!isRunning}>
      {/* Header — always visible, clickable to toggle steps */}
      <div
        className={cn('flex flex-col gap-2 px-4 py-3', stepsOpen && 'border-b')}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
            onClick={() => setStepsOpen((v) => !v)}
          >
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">{title}</span>
            {isJoined && (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                Following
              </span>
            )}
          </button>
          <div className="flex min-w-0 items-center gap-3">
            {allDone && isRunning ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <LoaderIcon className="size-3 animate-spin" />
                {displayStatus && <span className="shimmer shimmer-speed-80">{displayStatus}</span>}
              </span>
            ) : (
              <ElapsedTimeBadge startedAt={startedAt} completedAt={completedAt} isRunning={isRunning} />
            )}
            {allDone ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-success">
                <CheckIcon className="size-3" strokeWidth={2.5} />
                {statusBadge}
              </span>
            ) : isInterrupted ? (
              <span className="inline-flex items-center gap-1 truncate rounded bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning">
                <ClockIcon className="size-3 shrink-0" />
                <span className="truncate">Waiting for approval</span>
              </span>
            ) : !isRunning && completedCount > 0 ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-warning">
                <CircleAlertIcon className="size-3" />
                {statusBadge}
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground">{statusBadge}</span>
            )}
            {showStop && (
              <button
                type="button"
                onClick={onStop}
                className="shrink-0 cursor-pointer rounded-sm bg-primary/[0.08] px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-primary/[0.15] hover:text-primary"
                aria-label="Stop response"
              >
                Stop
              </button>
            )}
            <button
              type="button"
              className="shrink-0 cursor-pointer"
              onClick={() => setStepsOpen((v) => !v)}
              aria-label={stepsOpen ? 'Collapse plan' : 'Expand plan'}
            >
              <ChevronDownIcon
                className={cn(
                  'size-3 text-muted-foreground transition-transform duration-150',
                  stepsOpen && 'rotate-180',
                )}
              />
            </button>
          </div>
        </div>

        {/* Progress bar — always visible */}
        {!allDone && (
          <div className="h-[3px] overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-600 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}
      </div>

      {/* Steps — collapsible */}
      {stepsOpen && (
        <div className="overflow-hidden px-4 py-2">
          <ol className="flex list-none flex-col">
            {visibleSteps.map((step, i) => (
              <StepRow
                key={i}
                step={step}
                isActive={i === activeIndex}
                isLast={i === visibleSteps.length - 1 && hiddenCount === 0}
                displayStatus={i === activeIndex ? displayStatus : null}
                isRunning={isRunning}
                isInterrupted={isInterrupted}
              />
            ))}
          </ol>

          {needsCollapse && (
            <button
              type="button"
              className="mt-1 cursor-pointer pl-[30px] text-[12px] font-medium text-primary hover:underline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Show less' : `${hiddenCount} more steps`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
